import { useEffect, useState, type FormEvent } from "react";
import { AlertTriangle, CheckCircle2, Eye, FolderOpen, KeyRound, LoaderCircle, MonitorSmartphone, RefreshCw, ShieldCheck, Trash2, UserPlus, Users, X } from "lucide-react";
import { api } from "./api";
import type { ManagedUser, User, UserTutorialProgress } from "./types";
import { formatDate } from "./utils";

type Tone = "success" | "error" | "info";

export function ManageUsersView({ currentUser, notify }: { currentUser: User; notify: (message: string, tone?: Tone) => void }) {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [limits, setLimits] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [creating, setCreating] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [progressByUser, setProgressByUser] = useState<Record<string, UserTutorialProgress>>({});
  const [progressUser, setProgressUser] = useState<UserTutorialProgress | null>(null);
  const [createForm, setCreateForm] = useState({ username: "", password: "", deviceLimit: 1 });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmation: "" });

  async function load() {
    setLoading(true);
    try {
      const [result, progressResult] = await Promise.all([api.users(), api.userProgress()]);
      setUsers(result.users);
      setLimits(Object.fromEntries(result.users.map((user) => [user.id, user.deviceLimit])));
      const progress = Object.fromEntries(progressResult.users.map((user) => [user.userId, user]));
      setProgressByUser(progress);
      setProgressUser((current) => current ? progress[current.userId] ?? null : null);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to load users.", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function createReader(event: FormEvent) {
    event.preventDefault();
    setCreating(true);
    try {
      await api.createUser(createForm);
      setCreateForm({ username: "", password: "", deviceLimit: 1 });
      notify("Reader account created.", "success");
      await load();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to create the reader.", "error");
    } finally {
      setCreating(false);
    }
  }

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmation) {
      notify("New password and confirmation do not match.", "error");
      return;
    }
    setChangingPassword(true);
    try {
      await api.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmation: "" });
      notify("Your password was updated. Other sessions were signed out.", "success");
      await load();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to update your password.", "error");
    } finally {
      setChangingPassword(false);
    }
  }

  async function saveLimit(user: ManagedUser) {
    const deviceLimit = limits[user.id] ?? user.deviceLimit;
    setSavingId(user.id);
    try {
      await api.updateUser(user.id, { deviceLimit });
      notify(`Device limit for ${user.username} is now ${deviceLimit}.`, "success");
      await load();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to update the device limit.", "error");
    } finally {
      setSavingId("");
    }
  }

  async function revoke(user: ManagedUser) {
    if (!window.confirm(`Sign ${user.username} out on every active device?`)) return;
    setSavingId(user.id);
    try {
      await api.revokeUserDevices(user.id);
      notify(`All sessions for ${user.username} were revoked.`, "success");
      await load();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to revoke devices.", "error");
    } finally {
      setSavingId("");
    }
  }

  async function remove(user: ManagedUser) {
    if (!window.confirm(`Delete reader “${user.username}”? This cannot be undone.`)) return;
    setSavingId(user.id);
    try {
      await api.deleteUser(user.id);
      notify(`${user.username} was deleted.`, "success");
      await load();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to delete the user.", "error");
    } finally {
      setSavingId("");
    }
  }

  return (
    <div className="page-stack">
      <div className="page-heading">
        <div><span className="eyebrow">Identity &amp; access</span><h1>Manage users</h1><p>Create read-only accounts, control active devices, and rotate your administrator password.</p></div>
        <button className="secondary-button" onClick={() => void load()} disabled={loading}><RefreshCw className={loading ? "spin" : ""} size={16} />Refresh</button>
      </div>

      <section className="access-summary-grid">
        <article className="card access-summary"><div className="settings-icon"><ShieldCheck size={19} /></div><div><span className="muted-label">Administrator</span><strong>Full workspace control</strong><p>Upload, organize, configure storage, and manage users.</p></div></article>
        <article className="card access-summary"><div className="settings-icon blue"><Eye size={19} /></div><div><span className="muted-label">Reader</span><strong>View and download only</strong><p>No uploads, edits, deletions, storage settings, or user management.</p></div></article>
        <article className="card access-summary"><div className="settings-icon purple"><MonitorSmartphone size={19} /></div><div><span className="muted-label">Device policy</span><strong>1–20 devices per user</strong><p>New devices are blocked when a user reaches the configured limit.</p></div></article>
      </section>

      <section className="settings-grid manage-users-grid">
        <form className="card settings-card secure-form" onSubmit={changePassword}>
          <div className="settings-title"><div className="settings-icon"><KeyRound size={18} /></div><div><h2>Change my password</h2><span>Other devices are signed out after the change</span></div></div>
          <label>Current password<input type="password" autoComplete="current-password" value={passwordForm.currentPassword} onChange={(event) => setPasswordForm((form) => ({ ...form, currentPassword: event.target.value }))} required /></label>
          <label>New password<input type="password" autoComplete="new-password" minLength={12} maxLength={256} value={passwordForm.newPassword} onChange={(event) => setPasswordForm((form) => ({ ...form, newPassword: event.target.value }))} required /></label>
          <label>Confirm new password<input type="password" autoComplete="new-password" minLength={12} maxLength={256} value={passwordForm.confirmation} onChange={(event) => setPasswordForm((form) => ({ ...form, confirmation: event.target.value }))} required /></label>
          <button className="primary-button" type="submit" disabled={changingPassword}>{changingPassword ? <LoaderCircle className="spin" size={17} /> : <KeyRound size={17} />}Update password</button>
        </form>

        <form className="card settings-card secure-form" onSubmit={createReader}>
          <div className="settings-title"><div className="settings-icon blue"><UserPlus size={18} /></div><div><h2>Create reader</h2><span>Read-only access to uploaded content</span></div></div>
          <label>Username<input autoComplete="off" minLength={3} maxLength={32} pattern="[A-Za-z0-9._-]+" value={createForm.username} onChange={(event) => setCreateForm((form) => ({ ...form, username: event.target.value }))} placeholder="for example: team-reader" required /></label>
          <label>Temporary password<input type="password" autoComplete="new-password" minLength={12} maxLength={256} value={createForm.password} onChange={(event) => setCreateForm((form) => ({ ...form, password: event.target.value }))} required /></label>
          <label>Device limit<input type="number" min={1} max={20} value={createForm.deviceLimit} onChange={(event) => setCreateForm((form) => ({ ...form, deviceLimit: Number(event.target.value) }))} required /></label>
          <div className="form-note"><Eye size={15} />Readers can browse, preview, and download. Settings and file changes are blocked by the API.</div>
          <button className="primary-button" type="submit" disabled={creating}>{creating ? <LoaderCircle className="spin" size={17} /> : <UserPlus size={17} />}Create reader</button>
        </form>
      </section>

      <section className="card users-card">
        <div className="card-heading"><div><span className="muted-label">Workspace access</span><h2>Users and devices</h2></div><span className="subtle-chip"><Users size={14} />{users.length} {users.length === 1 ? "user" : "users"}</span></div>
        {loading ? <div className="loading-inline"><LoaderCircle className="spin" size={20} />Loading users…</div> : users.length ? <div className="users-table">
          <div className="users-table-head"><span>User</span><span>Access</span><span>Active devices</span><span>Progress</span><span>Device limit</span><span>Actions</span></div>
          {users.map((user) => <div className="users-table-row" key={user.id}>
            <div className="managed-user-name"><strong>{user.username}</strong><span>{user.lastLoginAt ? `Last login ${formatDate(user.lastLoginAt)}` : "Has not signed in yet"}</span></div>
            <span className={`role-chip ${user.role}`}>{user.role === "admin" ? <ShieldCheck size={13} /> : <Eye size={13} />}{user.role === "admin" ? "Administrator" : "Reader"}</span>
            <span className="device-count"><MonitorSmartphone size={15} />{user.activeDevices} active</span>
            {user.role === "reader" ? <button className="user-progress-button" onClick={() => setProgressUser(progressByUser[user.id] ?? { userId: user.id, username: user.username, completed: 0, total: 0, percent: 0, folders: [] })}><span><strong>{progressByUser[user.id]?.percent ?? 0}%</strong><small>{progressByUser[user.id]?.completed ?? 0}/{progressByUser[user.id]?.total ?? 0} items</small></span></button> : <span className="file-list-muted">—</span>}
            <div className="limit-editor"><input aria-label={`Device limit for ${user.username}`} type="number" min={1} max={20} value={limits[user.id] ?? user.deviceLimit} onChange={(event) => setLimits((current) => ({ ...current, [user.id]: Number(event.target.value) }))} /><button className="secondary-button compact" disabled={savingId === user.id || limits[user.id] === user.deviceLimit} onClick={() => void saveLimit(user)}>{savingId === user.id ? <LoaderCircle className="spin" size={14} /> : <CheckCircle2 size={14} />}Save</button></div>
            <div className="managed-user-actions">
              {user.id !== currentUser.id && <button className="secondary-button compact" disabled={savingId === user.id || user.activeDevices === 0} onClick={() => void revoke(user)}><MonitorSmartphone size={14} />Revoke</button>}
              {user.role === "reader" && <button className="icon-button small danger-icon" disabled={savingId === user.id} aria-label={`Delete ${user.username}`} onClick={() => void remove(user)}><Trash2 size={15} /></button>}
            </div>
          </div>)}
        </div> : <div className="empty-state compact"><AlertTriangle size={20} /><strong>No users found</strong><span>Create a reader account to begin.</span></div>}
      </section>
      {progressUser && <UserProgressModal progress={progressUser} onClose={() => setProgressUser(null)} />}
    </div>
  );
}

function UserProgressModal({ progress, onClose }: { progress: UserTutorialProgress; onClose: () => void }) {
  return <div className="modal-scrim" onClick={onClose}><section className="folder-access-modal user-progress-modal" role="dialog" aria-modal="true" aria-labelledby="user-progress-title" onClick={(event) => event.stopPropagation()}><div className="preview-head"><div><span className="muted-label">Tutorial completion</span><h2 id="user-progress-title">{progress.username} · {progress.percent}% complete</h2></div><button className="icon-button" aria-label="Close progress" onClick={onClose}><X size={19} /></button></div><div className="user-progress-body"><div className="progress-overview"><div className="progress-ring-copy"><strong>{progress.percent}%</strong><span>{progress.completed} of {progress.total} items completed</span></div></div>{progress.folders.length ? <div className="progress-folder-list">{progress.folders.map((folder) => <article className="progress-folder-row" key={folder.folderId}><div className="progress-folder-icon"><FolderOpen size={18} /></div><div><strong>{folder.folderName}</strong><span>{folder.completed} of {folder.total} items completed</span></div><b>{folder.percent}%</b></article>)}</div> : <div className="empty-state compact"><FolderOpen size={21} /><strong>No assigned folders</strong><span>Assign a tutorial folder to this reader to begin tracking progress.</span></div>}</div></section></div>;
}
